import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Slider from 'react-slick';
import { GridRow, GridColumn, Grid } from 'semantic-ui-react';

import AppHelmet from 'app/components/AppHelmet';
import IntlCrawlableLink from 'app/components/IntlCrawlableLink';
import Button from 'app/components/UIComponents/Button';
import Hook from 'app/components/UIComponents/Hook';
import Gradient from 'app/components/UIComponents/Gradient';
import CardsList from 'app/components/UIComponents/CardsList';
import Card from 'app/components/UIComponents/CardsList/Card';
import { PRODUCT_PICOX_ACQ } from 'app/components/Form/SendRequestForm';

import ImgLogoPicoxACQ from './images/logo-picoxia-acq.svg?url';
import ViewerScreenshotEn from './images/viewer-screenshot-en.png';
import WorklistScreenshotEn from './images/worklist-screenshot-en.png';
import CloudIcon from './images/icon-cloud.svg?url';
import VetIcon from './images/icon-vet.svg?url';
import AIIcon from './images/icon-ai.svg?url';
import RadioIcon from './images/icon-radio.svg?url';
import RadioImage1 from './images/radio1.jpg';
import RadioImage2 from './images/radio2.jpg';
import RadioImage3 from './images/radio3.jpg';
import RadioImage4 from './images/radio4.jpg';
import RadioImage5 from './images/radio5.jpg';
import LogoAssistovet from './images/pms/logo-assistovet.svg?url';
import LogoGmVet from './images/pms/logo-gmvet.png';
import LogoVetoPartner from './images/pms/logo-vetopartner.svg?url';
import LogoVetoWin from './images/pms/logo-vetowin.png';
import MultiSupportIllustration from './images/multi-support.png';

import './style.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const PicoxIAAcquisitionPage: FC = () => {
  const sliderSettings: import('react-slick').Settings = {
    dots: false,
    infinite: true,
    arrows: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
  };

  const intl = useIntl();

  const radioImages: string[] = [RadioImage1, RadioImage3, RadioImage5, RadioImage2, RadioImage4];

  return (
    <div className="picoxia">
      <AppHelmet
        titleIntlID="picoxIAAcquisition.metaTitle"
        descriptionIntlID="picoxIAAcquisition.metaDescription"
      />
      <div className="picoxiaAcquisition">
        <section id="introduction" className="container pageHeader">
          <Grid stackable columns={2}>
            <GridColumn>
              <Grid stackable>
                <GridRow>
                  <GridColumn width={4}>
                    <img src={ImgLogoPicoxACQ} alt="PicoxACQ" />
                  </GridColumn>
                  <GridColumn width={12}>
                    <h1 className="title title--big">
                      <FormattedMessage id="picoxIAAcquisition.header.title" />
                    </h1>
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn>
                    <Hook>
                      <FormattedMessage id="picoxIAAcquisition.header.hook" />
                    </Hook>
                    <p className="pageHeader__desc">
                      <FormattedMessage id="picoxIAAcquisition.header.text1" />{' '}
                      <IntlCrawlableLink url="/picoxia-intelligence">
                        <FormattedMessage id="picoxIAAcquisition.header.picoxIAIntelligenceLink" />
                      </IntlCrawlableLink>{' '}
                      <FormattedMessage id="picoxIAAcquisition.header.text2" />
                    </p>
                    <div className="pageHeader__buttons">
                      <Button url={`/request-demo?product=${PRODUCT_PICOX_ACQ}`}>
                        <FormattedMessage id="picoxIAAcquisition.header.button.requestDemo" />
                      </Button>
                    </div>
                  </GridColumn>
                </GridRow>
              </Grid>
            </GridColumn>
            <GridColumn verticalAlign="middle">
              <div className="pageHeader__screenshots">
                <img
                  src={WorklistScreenshotEn}
                  alt={intl.formatMessage({
                    id: 'picoxIAAcquisition.header.worklistScreenshot.alt',
                  })}
                  className="pageHeader__screenshot"
                />
                <img
                  src={ViewerScreenshotEn}
                  alt={intl.formatMessage({ id: 'picoxIAAcquisition.header.viewerScreenshot.alt' })}
                  className="pageHeader__screenshot"
                />
              </div>
            </GridColumn>
          </Grid>
        </section>

        <section id="state-of-the-art" className="container stateOfTheArt">
          <div className="stateOfTheArt__content">
            <h2 className="title">
              <FormattedMessage id="picoxIAAcquisition.stateOfTheArt.title" />
            </h2>
            <p>
              <FormattedMessage id="picoxIAAcquisition.stateOfTheArt.text" />
            </p>
          </div>
          <div className="stateOfTheArt__slider">
            <Slider {...sliderSettings}>
              {radioImages.map((radioImage) => (
                <img
                  key={radioImage}
                  src={radioImage}
                  alt={intl.formatMessage({ id: 'picoxIAAcquisition.stateOfTheArt.radio.alt' })}
                />
              ))}
            </Slider>
          </div>
        </section>

        <Gradient />
        <section id="advantages" className="container advantages">
          <CardsList>
            <Card>
              <Card.Icon icon={RadioIcon} />
              <Card.Title>
                <FormattedMessage id="picoxIAAcquisition.advantages.simplicity.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxIAAcquisition.advantages.simplicity.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={AIIcon} />
              <Card.Title>
                <FormattedMessage id="picoxIAAcquisition.advantages.integrated.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxIAAcquisition.advantages.integrated.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={CloudIcon} />
              <Card.Title>
                <FormattedMessage id="picoxIAAcquisition.advantages.cloud.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxIAAcquisition.advantages.cloud.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={VetIcon} />
              <Card.Title>
                <FormattedMessage id="picoxIAAcquisition.advantages.byVets.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxIAAcquisition.advantages.byVets.text" />
              </p>
            </Card>
          </CardsList>
        </section>

        <section id="unique-solution" className="container uniqueSolution">
          <div className="uniqueSolution__content">
            <h2 className="title">
              <FormattedMessage id="picoxIAAcquisition.uniqueSolution.title" />
            </h2>
            <p>
              <FormattedMessage id="picoxIAAcquisition.uniqueSolution.text" />
            </p>
          </div>
          <div>
            <img
              src={MultiSupportIllustration}
              alt={intl.formatMessage({
                id: 'picoxIAAcquisition.uniqueSolution.multisupportIllustration.alt',
              })}
              className="uniqueSolution__illustration"
            />
          </div>
        </section>

        <section id="pms" className="container interfaced">
          <div className="interfaced__content">
            <h2 className="title">
              <FormattedMessage id="picoxIAAcquisition.interfaced.title" />
            </h2>
            <p>
              <FormattedMessage id="picoxIAAcquisition.interfaced.text" />
            </p>
            <div className="interfaced__separation" />
            <div className="interfaced__logos">
              <img src={LogoAssistovet} alt="Assistovet" className="interfaced__logo" />
              <img src={LogoGmVet} alt="GmVet" className="interfaced__logo" />
              <img src={LogoVetoPartner} alt="VetoPartner" className="interfaced__logo" />
              <img src={LogoVetoWin} alt="VetoWin" className="interfaced__logo" />
            </div>
            <Hook>
              <FormattedMessage id="picoxIAAcquisition.interfaced.hook" />
            </Hook>
            <div className="uniqueSolution__buttons">
              <Button url={`/request-demo?product=${PRODUCT_PICOX_ACQ}`}>
                <FormattedMessage id="picoxIAAcquisition.interfaced.button.contact" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
export default PicoxIAAcquisitionPage;
